import React, { PropsWithChildren, ReactNode, useMemo, useRef } from "react";
import { PTooltipStyle as S } from "./PTooltip.style";
import { Tooltip, TooltipProps } from "primereact/tooltip";
import { useClassNames } from "hooks/useClassNames";
import { useClickOutside } from "primereact/hooks";

export type PTooltipProps = TooltipProps & {
  position?: "left" | "top" | "bottom" | "right";
  severity?: "primary" | "secondary";
  size?: "small" | "normal" | "large";
};

export const PTooltip = React.forwardRef<Tooltip, TooltipProps>((props, ref) => (
  <S.PTooltip {...props} ref={ref} />
));

export const PTooltipWrapped: React.FC<
  PropsWithChildren<
    {
      wrapperClassName?: string;
      materialIcon?: string;
      tooltip: ReactNode | string;
      disabled?: boolean;
    } & PTooltipProps
  >
> = ({
  wrapperClassName,
  tooltip,
  children,
  materialIcon,
  className,
  severity,
  size,
  showOnDisabled,
  disabled,
  ...props
}) => {
  const id = useMemo(
    () => `tooltip-wrapper-${new Date().getTime()}-${Math.round(Math.random() * 100000)}`,
    [tooltip],
  );

  const wrapperClass = useClassNames(
    {
      "material-symbols-sharp": !!materialIcon,
    },
    wrapperClassName,
  );
  const classNames = useClassNames(
    {
      "p-tooltip-secondary": severity === "secondary",
      "p-tooltip-follow-mouse": !!props.mouseTrack,
      "p-tooltip-small": size === "small",
      "p-tooltip-normal": !size || size === "normal",
      "p-tooltip-large": size === "large",
    },
    className,
  );
  const ref = useRef<HTMLDivElement>(null as never);
  const tooltipRef = useRef<Tooltip>(null as never);
  useClickOutside(ref, () => {
    tooltipRef.current?.hide();
  });

  return (
    <>
      {!disabled && (
        <PTooltip
          showDelay={props.showDelay ?? 150}
          hideDelay={100}
          {...(props as TooltipProps)}
          ref={tooltipRef}
          className={classNames}
          target={`#${id}`}
        >
          {tooltip}
        </PTooltip>
      )}
      {materialIcon ? (
        <S.TooltipWrapperI id={id} className={wrapperClass}>
          {materialIcon}
        </S.TooltipWrapperI>
      ) : (
        <S.TooltipWrapper
          ref={ref}
          onMouseLeave={() => {
            tooltipRef.current?.hide();
          }}
          className={wrapperClass}
          id={id}
          style={{ pointerEvents: showOnDisabled ? "auto" : "inherit" }}
        >
          {children}
        </S.TooltipWrapper>
      )}
    </>
  );
};
